import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Clock, ArrowRight, Sparkles } from "lucide-react";
import Seo from "@/components/seo/Seo";
import { getHeroImage, getHeroImageAbsolute } from "@/data/blogImages";
import { fetchBlogPost, fetchRelatedPosts } from "@/lib/blog-api";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [state, setState] = useState("loading"); // loading | ready | not-found

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    setPost(null);
    setRelated([]);

    fetchBlogPost(slug)
      .then((p) => {
        if (cancelled) return;
        setPost(p);
        setState("ready");
        return fetchRelatedPosts(slug, 3).catch(() => []);
      })
      .then((rel) => {
        if (!cancelled && rel) setRelated(rel);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.status === 404) setState("not-found");
        else setState("not-found");
      });
    return () => { cancelled = true; };
  }, [slug]);

  if (state === "loading") {
    return (
      <div
        data-testid="blog-post-loading"
        className="min-h-[60vh] flex items-center justify-center"
      >
        <div className="h-8 w-8 rounded-full border-2 border-slate-200 border-t-[#0A1128] animate-spin" />
      </div>
    );
  }
  if (state === "not-found" || !post) {
    return <Navigate to="/blog" replace />;
  }

  const publishedIso = new Date(post.published_at).toISOString();

  return (
    <>
      <Seo
        title={post.title}
        description={post.description}
        path={`/blog/${post.slug}`}
        type="article"
        image={getHeroImageAbsolute(post.slug) || undefined}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.description,
            "keywords": (post.keywords || []).join(", "),
            "articleSection": post.category,
            "wordCount": (post.content || "").trim().split(/\s+/).length,
            "datePublished": publishedIso,
            "dateModified": publishedIso,
            "author": { "@type": "Organization", "name": post.author },
            "publisher": {
              "@type": "Organization",
              "name": "AtlasForge Financial",
              "logo": {
                "@type": "ImageObject",
                "url": "https://atlasforgefinancial.com/logo512.png",
              },
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://atlasforgefinancial.com/blog/${post.slug}`,
            },
          },
        ]}
      />

      <article className="bg-white">
        <header className="relative overflow-hidden">
          <div className="absolute inset-0 af-soft-glow pointer-events-none" />
          <div className="relative mx-auto max-w-3xl px-6 lg:px-10 pt-16 pb-10">
            <Link
              to="/blog"
              data-testid="blog-post-back"
              className="inline-flex items-center gap-1.5 text-[13px] text-slate-500 hover:text-[#0A1128] transition-colors"
            >
              <ArrowLeft className="h-4 w-4" /> All posts
            </Link>

            <div className="mt-8 flex items-center gap-3 text-[12px] text-slate-500">
              <span className="uppercase tracking-[0.18em]">{post.category}</span>
              <span>·</span>
              <time dateTime={publishedIso}>
                {new Date(post.published_at).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </time>
              <span>·</span>
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" /> {post.read_minutes} min read
              </span>
            </div>

            <h1
              data-testid="blog-post-title"
              className="font-display mt-4 text-[36px] sm:text-[44px] lg:text-[52px] leading-[1.05] text-[#0A1128]"
            >
              {post.title}
            </h1>

            {post.excerpt && (
              <p className="mt-5 text-[17px] text-slate-600 leading-relaxed">{post.excerpt}</p>
            )}

            <div className="mt-8 text-[13.5px] text-slate-500">
              By <span className="text-[#0A1128] font-medium">{post.author}</span>
            </div>

            {getHeroImage(post.slug) && (
              <img
                src={getHeroImage(post.slug)}
                alt={post.title}
                loading="eager"
                fetchPriority="high"
                data-testid="blog-post-hero-image"
                className="mt-10 aspect-[16/9] w-full object-cover rounded-2xl af-shadow-card"
              />
            )}
          </div>
        </header>

        <div className="mx-auto max-w-6xl px-6 lg:px-10 pb-16 lg:grid lg:grid-cols-[minmax(0,1fr)_280px] lg:gap-14">
          <div className="min-w-0">
          <div className="af-prose" data-testid="blog-post-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
          </div>

          {/* Share CTA */}
          <div className="mt-12 rounded-3xl border border-slate-200 bg-[#F7F9FC] p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="font-display text-[20px] text-[#0A1128]">Ready to build on AtlasForge?</p>
              <p className="text-[14px] text-slate-600 mt-1">
                Get sandbox API keys in 60 seconds — or install the Safe to Spend 365 app.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/portal/login"
                data-testid="blog-post-cta-portal"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#0A1128] text-white px-5 py-2.5 text-[13.5px] font-medium hover:bg-[#1A4232] transition-colors"
              >
                Get API keys <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/safe-to-spend"
                data-testid="blog-post-cta-app"
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-[13.5px] font-medium text-[#0A1128] hover:border-[#0A1128] transition-colors"
              >
                Try Safe to Spend 365
              </Link>
            </div>
          </div>
          </div>

          {/* Related by keyword — sticky sidebar (desktop) */}
          {related.length > 0 && (
            <aside
              aria-label="Related posts by keyword"
              data-testid="blog-related-sidebar"
              className="hidden lg:block"
            >
              <div className="sticky top-24">
                <div className="rounded-2xl border border-slate-200 bg-white p-6">
                  <div className="flex items-center gap-2 text-[11.5px] uppercase tracking-[0.18em] text-slate-500">
                    <Sparkles className="h-3.5 w-3.5 text-[#FF5A36]" />
                    Related by keyword
                  </div>
                  <p className="mt-2 font-display text-[16px] leading-snug text-[#0A1128]">
                    Adjacent reads for this topic
                  </p>
                  <ul className="mt-5 space-y-4">
                    {related.map((p) => (
                      <li key={p.slug}>
                        <Link
                          to={`/blog/${p.slug}`}
                          data-testid={`blog-related-side-${p.slug}`}
                          className="group block"
                        >
                          <p className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500">
                            {p.category}
                          </p>
                          <p className="mt-1 text-[13.5px] font-medium leading-snug text-[#0A1128] group-hover:text-[#1A4232] transition-colors">
                            {p.title}
                          </p>
                          <span className="mt-1.5 inline-flex items-center gap-1 text-[11.5px] text-slate-500 group-hover:text-[#0A1128] group-hover:gap-1.5 transition-all">
                            <Clock className="h-3 w-3" />
                            {p.read_minutes} min read
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/blog"
                    data-testid="blog-related-side-view-all"
                    className="mt-6 inline-flex items-center gap-1 text-[12px] font-medium text-[#0A1128] hover:gap-2 transition-all"
                  >
                    Browse all posts <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            </aside>
          )}
        </div>

        {related.length > 0 && (
          <section className="bg-[#F7F9FC]">
            <div className="mx-auto max-w-6xl px-6 lg:px-10 py-16">
              <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">Keep reading</p>
              <h2 className="font-display text-[26px] text-[#0A1128] mt-2">Related posts</h2>
              <div className="mt-8 grid md:grid-cols-3 gap-6">
                {related.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/blog/${p.slug}`}
                    data-testid={`blog-related-${p.slug}`}
                    className="group rounded-2xl border border-slate-200 bg-white p-6 hover:-translate-y-1 transition-transform af-shadow-card"
                  >
                    <div className="text-3xl mb-3">{p.hero_emoji || "📰"}</div>
                    <p className="text-[11.5px] uppercase tracking-[0.14em] text-slate-500">{p.category}</p>
                    <h3 className="font-display text-[18px] leading-[1.2] text-[#0A1128] mt-1.5 group-hover:text-[#1A4232] transition-colors">
                      {p.title}
                    </h3>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-[#0A1128] group-hover:gap-2.5 transition-all">
                      Read <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </article>
    </>
  );
}
